import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

export interface RetentionEncounter {
  encounterId: string;
  recordedAt: string;
  expiresAt: string;
  daysRemaining: number;
  status: 'info' | 'warning' | 'critical';
  isSigned: boolean;
}

export interface RetentionSummary {
  expiringIn7Days: number;
  expiringIn3Days: number;
  expiringToday: number;
  totalActive: number;
}

export interface RetentionAlerts {
  enabled: boolean;
  standalone: boolean;
  retentionDays: number;
  summary: RetentionSummary;
  encounters: RetentionEncounter[];
}

export function useRetentionAlerts(autoRefreshMs = 5 * 60 * 1000) {
  const [data, setData] = useState<RetentionAlerts | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await apiService.get<{ success: boolean; data: RetentionAlerts }>('/v1/dashboard/retention-alerts');
      if (res.success) {
        setData(res.data);
      }
    } catch {
      // Silently fail — retention alerts are non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, autoRefreshMs);
    return () => clearInterval(interval);
  }, [fetchAlerts, autoRefreshMs]);

  return {
    alerts: data,
    enabled: data?.enabled ?? false,
    standalone: data?.standalone ?? false,
    retentionDays: data?.retentionDays ?? 30,
    summary: data?.summary ?? null,
    encounters: data?.encounters ?? [],
    loading,
    refresh: fetchAlerts,
  };
}
